import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

function formatMoney(amountMinor, currency) {
  if (amountMinor == null || !currency) return "";
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return `${amountMinor / 100} ${currency}`;
  }
}

export default function CheckoutStart() {
  const { offerKey } = useParams();
  const { user, initializing } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [digitalAccepted, setDigitalAccepted] = useState(false);
  const [offer, setOffer] = useState(null);
  const [loadingOffer, setLoadingOffer] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const provider = params.get("provider");
  const isStripe = provider === "stripe";
  const validProvider = provider === "stripe" || provider === "prodamus";
  const price = useMemo(
    () => offer ? formatMoney(offer.amount_minor, offer.currency) : "",
    [offer]
  );

  const productReturnPath = offerKey === "almond" ? "/almond-landing/#access" : "/programs";
  const productReturnLabel = offerKey === "almond" ? "Вернуться к Тому I «Миндаль»" : "Вернуться к программам";

  useEffect(() => {
    if (!validProvider || !offerKey) {
      setLoadingOffer(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/payments/offers/${encodeURIComponent(offerKey)}?provider=${encodeURIComponent(provider)}`
        );
        if (!cancelled) setOffer(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoadingOffer(false);
      }
    })();
    return () => { cancelled = true; };
  }, [offerKey, provider, validProvider]);

  useEffect(() => {
    if (initializing || !validProvider || !offerKey) return;
    if (!user) {
      try {
        sessionStorage.setItem(
          "post_login_redirect",
          `/checkout/${encodeURIComponent(offerKey)}?provider=${encodeURIComponent(provider)}`
        );
      } catch {}
      nav("/login", { replace: true });
    }
  }, [initializing, user, provider, validProvider, nav, offerKey]);

  const startPayment = async () => {
    setError("");
    if (!offerAccepted) {
      setError("Чтобы продолжить, примите Публичную оферту.");
      return;
    }
    if (isStripe && !digitalAccepted) {
      setError("Для международной покупки требуется отдельное согласие на немедленное предоставление цифрового контента.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/payments/checkout/${encodeURIComponent(offerKey)}`, {
        provider,
        offer_accepted: true,
        digital_content_early_access_accepted: isStripe,
      });
      if (!data?.checkout_url) throw new Error("Платёжная ссылка не получена");
      window.location.assign(data.checkout_url);
    } catch (e) {
      setError(formatApiError(e));
      setLoading(false);
    }
  };

  if (!validProvider || !offerKey) {
    return (
      <main className="min-h-screen bg-[#21150D] text-[#F5EAD8] flex items-center justify-center px-6">
        <section className="w-full max-w-xl text-center">
          <h1 className="font-serif text-4xl">Способ оплаты не найден</h1>
          <Link to="/programs" className="inline-block mt-8 underline underline-offset-4">Вернуться к программам</Link>
        </section>
      </main>
    );
  }

  if (initializing || !user || loadingOffer) {
    return (
      <main className="min-h-screen bg-[#21150D] text-[#F5EAD8] flex items-center justify-center px-6">
        <p className="text-[#F5EAD8]/70">Проверяем покупку…</p>
      </main>
    );
  }

  if (!offer) {
    return (
      <main className="min-h-screen bg-[#21150D] text-[#F5EAD8] flex items-center justify-center px-6">
        <section className="w-full max-w-xl text-center">
          <h1 className="font-serif text-4xl">Покупка недоступна</h1>
          <p className="mt-5 text-[#F3C7B5]">{error || "Тариф не найден."}</p>
          <Link to="/programs" className="inline-block mt-8 underline underline-offset-4">Вернуться к программам</Link>
        </section>
      </main>
    );
  }

  const accessLabel =
    offer.access_type === "lifetime"
      ? "бессрочный доступ"
      : offer.access_period_days
        ? `доступ на ${offer.access_period_days} дней`
        : "доступ по подписке";

  return (
    <main className="min-h-screen bg-[#21150D] text-[#F5EAD8] flex items-center justify-center px-6 py-12">
      <section className="w-full max-w-2xl">
        <a
          href={productReturnPath}
          className="inline-flex items-center gap-2 text-sm text-[#F5EAD8]/70 hover:text-[#F5EAD8] transition-colors"
          data-testid="checkout-back-to-product"
        >
          <span aria-hidden="true">←</span>
          <span>{productReturnLabel}</span>
        </a>

        <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#C9A84C]">Anokina Studio</div>
        <h1 className="font-serif text-4xl mt-4">{offer.title}</h1>
        <p className="mt-3 text-[#F5EAD8]/70">
          {price} · {offer.billing_type === "subscription" ? "подписка" : accessLabel}
        </p>

        <div className="mt-8 space-y-5 text-sm leading-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={offerAccepted}
              onChange={(e) => setOfferAccepted(e.target.checked)}
              className="mt-1"
              data-testid="checkout-offer-consent"
            />
            <span>
              Подтверждаю заказ и принимаю условия{" "}
              <Link to="/legal/offer" target="_blank" className="underline underline-offset-4">
                Публичной оферты Anokina Studio
              </Link>.
            </span>
          </label>

          {isStripe && (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={digitalAccepted}
                onChange={(e) => setDigitalAccepted(e.target.checked)}
                className="mt-1"
                data-testid="checkout-digital-content-consent"
              />
              <span>
                Согласен(на) на предоставление цифрового контента до истечения срока для отказа от дистанционного договора
                и понимаю, что после начала предоставления доступа могу утратить право на отказ в случаях,
                предусмотренных применимым законодательством.
              </span>
            </label>
          )}
        </div>

        {error && (
          <div className="mt-6 rounded-lg border border-[#C98C73]/40 bg-[#C98C73]/10 px-4 py-3 text-sm text-[#F3C7B5]" role="alert">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={startPayment}
          disabled={loading}
          className="mt-8 border border-[#B9975C] px-7 py-4 text-sm uppercase tracking-[0.12em] text-[#D9BD82] disabled:opacity-50"
          data-testid="checkout-pay-button"
        >
          {loading ? "Открываем оплату…" : `Перейти к оплате — ${price}`}
        </button>

        <div className="mt-6 text-xs text-[#F5EAD8]/55">
          После подтверждения оплаты платёжной системой доступ будет открыт автоматически.
        </div>
      </section>
    </main>
  );
}
